if (!customElements.get('booking-calendar')) {
  class BookingCalendar extends HTMLElement {
    constructor() {
      super();

      const now = new Date();
      this.currentDate = new Date(now.getFullYear(), now.getMonth(), 1);
      this.selectedDate = null;
      this.selectedTime = null;

      // Configuration from data attributes
      this.closedDays = this.dataset.closedDays ? this.dataset.closedDays.split(',').map(d => parseInt(d.trim())) : [0, 1, 5, 6]; // Default: Sun, Mon, Fri, Sat
      this.timeSlots = this.dataset.timeSlots ? this.dataset.timeSlots.split(',').map(t => t.trim()) : ['10:00', '10:30', '11:00', '11:30', '12:00', '12:30', '13:00', '13:30', '14:00', '14:30'];
      this.minDaysAhead = parseInt(this.dataset.minDaysAhead) || 1;
      this.maxDaysAhead = parseInt(this.dataset.maxDaysAhead) || 60;

      this.monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
      this.dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    }

    connectedCallback() {
      this.cacheElements();
      this.bindEvents();
      this.render();
      this.handlePostedState();
      this.captureUtms();
    }

    captureUtms() {
      const field = this.querySelector('[data-utm-field]');
      if (!field) return;

      const KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 'fbclid'];
      const params = new URLSearchParams(window.location.search);
      const found = KEYS.filter(k => params.get(k))
        .map(k => `${k}=${params.get(k)}`)
        .join(' | ');

      // Persist for the session so the attribution survives browsing between pages
      try {
        if (found) sessionStorage.setItem('bw_campaign', found);
        field.value = found || sessionStorage.getItem('bw_campaign') || '';
      } catch (e) {
        field.value = found;
      }
    }

    handlePostedState() {
      // The form is always visible now; just bring the success/error
      // message into view after Shopify reloads the page
      const message = this.querySelector('.booking-calendar__message');
      if (!message) return;

      requestAnimationFrame(() => {
        message.scrollIntoView({ behavior: 'smooth', block: 'center' });
      });
    }

    cacheElements() {
      this.monthYearEl = this.querySelector('[data-month-year]');
      this.daysContainer = this.querySelector('[data-days]');
      this.timesContainer = this.querySelector('[data-times]');
      this.summaryEl = this.querySelector('[data-summary]');
      this.summaryDateEl = this.querySelector('[data-summary-date]');
      this.prevBtn = this.querySelector('[data-prev]');
      this.nextBtn = this.querySelector('[data-next]');
      this.form = this.querySelector('form');
      this.dateInput = this.querySelector('[name="contact[Preferred Date]"]');
      this.timeInput = this.querySelector('[name="contact[Preferred Time]"]');
    }

    bindEvents() {
      this.prevBtn?.addEventListener('click', () => this.changeMonth(-1));
      this.nextBtn?.addEventListener('click', () => this.changeMonth(1));

      this.form?.addEventListener('submit', (e) => this.handleSubmit(e));
    }

    render() {
      this.renderCalendar();
      this.renderTimeSlots();
      this.updateSummary();
      this.updateNavButtons();
    }

    renderCalendar() {
      const year = this.currentDate.getFullYear();
      const month = this.currentDate.getMonth();

      // Update month/year display
      if (this.monthYearEl) {
        this.monthYearEl.textContent = `${this.monthNames[month]} ${year}`;
      }

      // Get first day of month and total days
      const firstDay = new Date(year, month, 1);
      const lastDay = new Date(year, month + 1, 0);
      const startingDay = firstDay.getDay();
      const totalDays = lastDay.getDate();

      // Get days from previous month
      const prevMonthLastDay = new Date(year, month, 0).getDate();

      let html = '';

      // Previous month's trailing days
      for (let i = startingDay - 1; i >= 0; i--) {
        const day = prevMonthLastDay - i;
        html += `<button type="button" class="booking-calendar__day is-other-month" disabled>${day}</button>`;
      }

      // Current month's days
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const minDate = new Date(today);
      minDate.setDate(minDate.getDate() + this.minDaysAhead);

      const maxDate = new Date(today);
      maxDate.setDate(maxDate.getDate() + this.maxDaysAhead);

      for (let day = 1; day <= totalDays; day++) {
        const date = new Date(year, month, day);
        const dayOfWeek = date.getDay();

        const isToday = date.getTime() === today.getTime();
        const isClosed = this.closedDays.includes(dayOfWeek);
        const isPast = date < minDate;
        const isTooFar = date > maxDate;
        const isDisabled = isClosed || isPast || isTooFar;
        const isSelected = this.selectedDate &&
          this.selectedDate.getDate() === day &&
          this.selectedDate.getMonth() === month &&
          this.selectedDate.getFullYear() === year;

        let classes = ['booking-calendar__day'];
        if (isToday) classes.push('is-today');
        if (isSelected) classes.push('is-selected');
        if (isClosed && !isPast && !isTooFar) classes.push('is-unavailable');

        html += `<button
          type="button"
          class="${classes.join(' ')}"
          data-date="${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}"
          aria-label="${day} ${this.monthNames[month]} ${year}"
          aria-pressed="${isSelected ? 'true' : 'false'}"
          ${isDisabled ? 'disabled' : ''}
        >${day}</button>`;
      }

      // Next month's leading days (fill the final row only)
      const remainingCells = (7 - (startingDay + totalDays) % 7) % 7;
      for (let i = 1; i <= remainingCells; i++) {
        html += `<button type="button" class="booking-calendar__day is-other-month" disabled>${i}</button>`;
      }

      if (this.daysContainer) {
        this.daysContainer.innerHTML = html;

        // Bind click events to day buttons
        this.daysContainer.querySelectorAll('.booking-calendar__day:not([disabled])').forEach(btn => {
          btn.addEventListener('click', () => this.selectDate(btn.dataset.date));
        });
      }
    }

    renderTimeSlots() {
      if (!this.timesContainer) return;

      if (!this.selectedDate) {
        this.timesContainer.innerHTML = `
          <div class="booking-calendar__times-placeholder">
            Please select a date to see available times
          </div>
        `;
        return;
      }

      const html = this.timeSlots.map(time => {
        const isSelected = this.selectedTime === time;
        return `
          <button
            type="button"
            class="booking-calendar__time-slot${isSelected ? ' is-selected' : ''}"
            data-time="${time}"
            aria-pressed="${isSelected ? 'true' : 'false'}"
          >${this.formatTime(time)}</button>
        `;
      }).join('');

      this.timesContainer.innerHTML = `
        <div class="booking-calendar__times-header">Available Times</div>
        <div class="booking-calendar__times-grid">${html}</div>
      `;

      // Bind click events to time slots
      this.timesContainer.querySelectorAll('.booking-calendar__time-slot').forEach(btn => {
        btn.addEventListener('click', () => this.selectTime(btn.dataset.time));
      });
    }

    selectDate(dateString) {
      const [year, month, day] = dateString.split('-').map(Number);
      this.selectedDate = new Date(year, month - 1, day);
      this.selectedTime = null; // Reset time when date changes

      this.render();
    }

    selectTime(time) {
      this.selectedTime = time;

      // Update time slot UI
      this.timesContainer?.querySelectorAll('.booking-calendar__time-slot').forEach(btn => {
        const selected = btn.dataset.time === time;
        btn.classList.toggle('is-selected', selected);
        btn.setAttribute('aria-pressed', selected ? 'true' : 'false');
      });

      this.updateSummary();
    }

    updateSummary() {
      if (!this.summaryEl) return;

      const hasSelection = this.selectedDate && this.selectedTime;
      this.summaryEl.classList.toggle('is-hidden', !hasSelection);

      if (hasSelection && this.summaryDateEl) {
        const options = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' };
        const dateStr = this.selectedDate.toLocaleDateString('en-GB', options);
        const timeStr = this.formatTime(this.selectedTime);
        this.summaryDateEl.textContent = `${dateStr} at ${timeStr}`;
      }

      // Update hidden form fields
      if (this.dateInput && this.selectedDate) {
        const options = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' };
        this.dateInput.value = this.selectedDate.toLocaleDateString('en-GB', options);
      }

      if (this.timeInput && this.selectedTime) {
        this.timeInput.value = this.formatTime(this.selectedTime);
      }
    }

    changeMonth(delta) {
      this.currentDate = new Date(this.currentDate.getFullYear(), this.currentDate.getMonth() + delta, 1);
      this.render();
    }

    updateNavButtons() {
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const currentMonth = new Date(this.currentDate.getFullYear(), this.currentDate.getMonth(), 1);
      const thisMonth = new Date(today.getFullYear(), today.getMonth(), 1);

      const maxDate = new Date(today);
      maxDate.setDate(maxDate.getDate() + this.maxDaysAhead);
      const maxMonth = new Date(maxDate.getFullYear(), maxDate.getMonth(), 1);

      if (this.prevBtn) {
        this.prevBtn.disabled = currentMonth <= thisMonth;
      }

      if (this.nextBtn) {
        this.nextBtn.disabled = currentMonth >= maxMonth;
      }
    }

    formatTime(time24) {
      const [hours, minutes] = time24.split(':').map(Number);
      const period = hours >= 12 ? 'PM' : 'AM';
      const hours12 = hours % 12 || 12;
      return `${hours12}:${String(minutes).padStart(2, '0')} ${period}`;
    }

    handleSubmit(e) {
      if (!this.selectedDate || !this.selectedTime) {
        e.preventDefault();
        if (this.timesContainer) {
          let notice = this.timesContainer.querySelector('.booking-calendar__times-placeholder');
          if (!notice) {
            notice = document.createElement('div');
            notice.className = 'booking-calendar__times-placeholder';
            this.timesContainer.prepend(notice);
          }
          notice.textContent = 'Please select both a date and time for your consultation.';
          notice.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
        return;
      }

      // Prevent duplicate submissions while Shopify processes the contact form
      const submitBtn = this.form?.querySelector('[type="submit"]');
      if (submitBtn) {
        if (submitBtn.disabled) {
          e.preventDefault();
          return;
        }
        // Disable after this tick so the button's value still submits
        setTimeout(() => {
          submitBtn.disabled = true;
          submitBtn.textContent = 'Sending…';
        }, 0);
      }

      // Form will submit normally via Shopify's contact form
    }
  }

  customElements.define('booking-calendar', BookingCalendar);
}
